<style src="./style.scss">
</style>

<script>
  import { afterUpdate, onMount } from "svelte";
  import Spinner from "@components/spinner";
  import jwt_decode from "jwt-decode";
  import { navigate, useLocation } from "svelte-navigator";
  import { query, mutation } from "svelte-apollo";
  import { getEmail, removeToken, getToken, saveEmail } from "@services/auth";

  import { SUBSCRIPTION_QUERY } from "./subscription.query.ts";
  import { PROFILE_QUERY } from "./profile.query.ts";
  import { USER_QUERY } from "./user.query.ts";

  import { UPDATE_USER_NAME_MUTATION } from "./updateUserName.mutation.ts";
  import { UPDATE_USER_PHONE_MUTATION } from "./updateUserPhone.mutation.ts";
  import { UPDATE_USER_TITLE_MUTATION } from "./updateUserTitle.mutation.ts";
  import { UPDATE_USER_BIRTHDATE_MUTATION } from "./updateUserBirthDate.mutation.ts";
  import { UPDATE_USER_EMAIL_MUTATION } from "./updateUserEmail.mutation.ts";

  import NameModal from "./modals/name.svelte";
  import PhoneModal from "./modals/phone.svelte";
  import TitleModal from "./modals/title.svelte";
  import BirthDateModal from "./modals/birthdate.svelte";
  import EmailModal from './modals/email.svelte';

  const location = useLocation();

  let subscription;
  let profile;
  let isLoading = true;
  let billingInfo = {};
  let showBillingInfo = false;
  const email = getEmail();

  let isOpenUpdateUserNameModal = false;
  let isOpenUpdateUserPhoneModal = false;
  let isOpenUpdateUserTitleModal = false;
  let isOpenUpdateUserBirthDateModal = false;
  let shouldOpenEmailUpdateModal = false;
  let emailErrorMessage = '';

  const token = getToken();

  const subscriptionQuery = query(SUBSCRIPTION_QUERY);
  const userQuery = query(USER_QUERY, {
    variables: {},
    skip: true,
    context: { clientName: "core" },
  });

  const profileQuery = query(PROFILE_QUERY, {
    variables: {},
    skip: true,
    context: { clientName: "core" },
  });

  const updateUserName = mutation(UPDATE_USER_NAME_MUTATION);
  const updateUserPhone = mutation(UPDATE_USER_PHONE_MUTATION);
  const updateUserTitle = mutation(UPDATE_USER_TITLE_MUTATION);
  const updateUserBirthDate = mutation(UPDATE_USER_BIRTHDATE_MUTATION);
  const updateUserEmail = mutation(UPDATE_USER_EMAIL_MUTATION);

  if (!token) {
    navigate("/");
  }

  afterUpdate(() => {
    if (
      $subscriptionQuery?.data?.subscription !== null &&
      $profileQuery?.data?.profile !== null
    ) {
      subscription = $subscriptionQuery?.data?.subscription;
      profile = $profileQuery?.data?.profile;
    }

    if ($subscriptionQuery?.data?.subscription === null) {
      removeToken();
      navigate("/");
    }
  });

  onMount(() => {
    fetchProfile();
    fetchBillingInfo();
  });

  function formatPhone(phone) {
    const value = phone.replace("+55", "");

    const formatted = value.replace(/^(\d{2})(\d{4})(\d{5}).*/, "($1) $2-$3");

    return formatted;
  }

  function isCanceled() {
    return (
      subscription.status === "CANCELED" || subscription.status === "REFUNDED"
    );
  }

  async function fetchBillingInfo() {
    const decoded = jwt_decode(token);
    const userId = decoded["https://cnago.com.br/uuid"];

    const { data } = await userQuery.refetch({ id: userId });

    const { customers } = data.user;

    const customer = customers.nodes[0];

    if (customer) {
      showBillingInfo = true;
      const address = customer.addresses.nodes[0]?.formattedAddress;

      billingInfo = {
        name: customer.name,
        cpf: customer.cpf,
        phone: customer.phone,
        email: customer.email,
        address,
      };

      return;
    }
  }

  const updateEmail = async (email) => {
    try {
      isLoading = true;
      emailErrorMessage = '';
      const { data } = await updateUserEmail({
        variables: {
          email,
        },
        context: { clientName: 'core' },
      });
      if (data.updateUserEmail) {
        shouldOpenEmailUpdateModal = false;
        saveEmail(email);
        return fetchProfile();
      }
    } catch(error) {
      isLoading = false;
      const { message } = error;
      if (message === 'E-mail is already in use') {
        emailErrorMessage = `Este e-mail <b>${email}</b>, já esta em uso. Por favor digite outro email e tente novamente.`;
      } else {
        emailErrorMessage = 'Não foi possível atualizar seu email neste momento, por favor tente novamente mais tarde.';
      }
    }
  }

  async function fetchProfile() {
    await profileQuery.refetch();

    isLoading = false;
  }

  async function updateName(name) {
    isLoading = true;
    const { data } = await updateUserName({
      variables: {
        name,
      },
      context: { clientName: "core" },
    });

    if (data.updateUserName) {
      isOpenUpdateUserNameModal = false;
      fetchProfile();
    }
  }

  async function updatePhone(phone) {
    isLoading = true;
    const { data } = await updateUserPhone({
      variables: {
        phone: `+55${phone}`,
      },
      context: { clientName: "core" },
    });

    if (data.updateUserPhone) {
      isOpenUpdateUserPhoneModal = false;
      fetchProfile();
    }
  }

  async function updateTitle(title) {
    isLoading = true;
    const { data } = await updateUserTitle({
      variables: {
        title,
      },
      context: { clientName: "core" },
    });

    if (data.updateUserTitle) {
      isOpenUpdateUserTitleModal = false;
      fetchProfile();
    }
  }

  async function updateBirthdate(birthdate) {
    isLoading = true;
    const { data } = await updateUserBirthDate({
      variables: {
        birthdate,
      },
      context: { clientName: "core" },
    });

    if (data.updateUserBirthDate) {
      isOpenUpdateUserBirthDateModal = false;
      fetchProfile();
    }
  }

  function normalizeDate(birthdate) {
    const date = new Date(birthdate);

    return new Date(date.setDate(date.getDate() + 1)).toLocaleDateString(
      "pt-br"
    );
  }
</script>

{#if isLoading}
  <Spinner />
{:else}
  <div class="account">
    <EmailModal
      isOpen={shouldOpenEmailUpdateModal}
      currentEmail={profile?.email}
      onClose={() => {
        shouldOpenEmailUpdateModal = false;
        emailErrorMessage = '';
      }}
      onSubmit="{updateEmail}"
      error="{emailErrorMessage}"
    />
    
    <NameModal
      isOpen="{isOpenUpdateUserNameModal}"
      onClose="{() => (isOpenUpdateUserNameModal = false)}"
      name="{profile?.name}"
      onSubmit="{updateName}" />

    <PhoneModal
      isOpen="{isOpenUpdateUserPhoneModal}"
      onClose="{() => (isOpenUpdateUserPhoneModal = false)}"
      onSubmit="{updatePhone}" />

    <TitleModal
      isOpen="{isOpenUpdateUserTitleModal}"
      onClose="{() => (isOpenUpdateUserTitleModal = false)}"
      title="{profile?.title}"
      onSubmit="{updateTitle}" />

    <BirthDateModal
      isOpen="{isOpenUpdateUserBirthDateModal}"
      onClose="{() => (isOpenUpdateUserBirthDateModal = false)}"
      onSubmit="{updateBirthdate}" />

    <div>
      <div>
        <div class="account__content__panel">
          <h1>Dados da conta</h1>
          <h2>Informações do perfil</h2>

          <div>
            <div>
              <div class="account-info spacing">
                <div>Nome completo</div>
                <div>{profile?.name || ""}</div>
                <a on:click="{() => (isOpenUpdateUserNameModal = true)}"
                  >Mudar nome completo</a>
              </div>

              <div class="account-info spacing">
                <div>Como podemos te chamar?</div>
                <div>{profile?.title || ""}</div>
                <a on:click="{() => (isOpenUpdateUserTitleModal = true)}">
                  Mudar
                </a>
              </div>

              <div class="account-info spacing">
                <div>E-mail</div>
                <div>{profile?.email}</div>
                <a on:click="{() => (shouldOpenEmailUpdateModal = true)}"
                  >Mudar email</a>
              </div>
            </div>
            <div>
              <div class="account-info spacing">
                <div>Telefone</div>
                <div>{profile?.phone ? formatPhone(profile.phone) : ""}</div>
                <a on:click="{() => (isOpenUpdateUserPhoneModal = true)}"
                  >Mudar telefone</a>
              </div>

              <div class="account-info spacing">
                <div>Data de nascimento</div>
                <div>
                  {profile?.birthdate ? normalizeDate(profile?.birthdate) : ""}
                </div>
                <a on:click="{() => (isOpenUpdateUserBirthDateModal = true)}">
                  Mudar data de nascimento
                </a>
              </div>
            </div>
          </div>

          {#if showBillingInfo}
            <h2>Informações de cobrança</h2>
            <div>
              <div>
                <div class="account-info">
                  <div>Nome completo</div>
                  <div>{billingInfo?.name || ""}</div>
                </div>

                <div class="account-info">
                  <div>E-mail</div>
                  <div>{billingInfo?.email || ""}</div>
                </div>

                <div class="account-info">
                  <div>Telefone</div>
                  <div>{billingInfo?.phone || ""}</div>
                </div>
              </div>
              <div>
                <div class="account-info">
                  <div>CPF</div>
                  <div>{billingInfo?.cpf || ""}</div>
                </div>

                <div class="account-info">
                  <div>Endereço</div>
                  <div>
                    {billingInfo?.address || ""}
                  </div>
                </div>
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}
